import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import SidebarLayout from '../../components/layout/SidebarLayout';
import { useAuth } from '../../context/AuthContext';
import { queueMutation } from '../../services/offlineQueue';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, Plus, Play, Users, Eye, Upload, Search, Loader2, ChevronDown, ChevronRight, Trash2, Heart, Clock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { useConfirm } from '../../components/common/ConfirmDialog';

const statusBadge = {
  pending: { label: 'Pending', className: 'bg-gray-100 text-gray-700' },
  in_progress: { label: 'In Progress', className: 'bg-blue-100 text-blue-700' },
  complete: { label: 'Complete', className: 'bg-green-100 text-green-700' },
  requeued: { label: 'Re-Queued', className: 'bg-yellow-100 text-yellow-700' },
};

const CardManagementPage = () => {
  const { isViewOnly, isTenantAdmin } = useAuth();
  const viewOnly = isViewOnly();

  const isSystemOwnerImpersonating = (() => {
    try {
      const originalUser = localStorage.getItem('original_user');
      if (!originalUser || !localStorage.getItem('impersonation_id')) return false;
      return JSON.parse(originalUser)?.roles?.includes('system_owner');
    } catch { return false; }
  })();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { saleId } = useParams();
  const [searchParams] = useSearchParams();
  const saleName = searchParams.get('name') || '';
  const qc = useQueryClient();

  const [saleNameResolved, setSaleNameResolved] = useState(saleName);
  const [isDigitalSale, setIsDigitalSale] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [ocrOpen, setOcrOpen] = useState(false);
  const [completedOpen, setCompletedOpen] = useState(false);
  const [completedSearch, setCompletedSearch] = useState('');
  const [virtualOpen, setVirtualOpen] = useState(false);

  // ── Server state ──────────────────────────────────────────────────────────
  const { data: cards = [], isLoading: loading } = useQuery({
    queryKey: QK.saleCards(saleId),
    queryFn: async () => {
      const [cardsRes, saleRes] = await Promise.all([
        api.get(`/sale-cards?sale_id=${saleId}`),
        api.get(`/sales/${saleId}`),
      ]);
      if (saleRes.data?.sale_name) setSaleNameResolved(saleRes.data.sale_name);
      if (saleRes.data?.is_digital_sale) setIsDigitalSale(true);
      // Enrich cards with viewer industry role in parallel
      const enriched = await Promise.all((cardsRes.data || []).map(async (card) => {
        if (card.viewer_crm_entity_id) {
          try {
            const e = await api.get(`/crm-entities/${card.viewer_crm_entity_id}`);
            return { ...card, viewer_role: e.data?.person_industry_role || e.data?.organization_industry_role || '' };
          } catch { return { ...card, viewer_role: '' }; }
        }
        return { ...card, viewer_role: '' };
      }));
      return enriched;
    },
    enabled: !!saleId,
    onError: (err) => console.error(err),
  });

  const invalidateCards = () => qc.invalidateQueries({ queryKey: QK.saleCards(saleId) });

  const statusOrder = { in_progress: 0, requeued: 1, pending: 2 };
  const activeCards = cards.filter(c => c.status !== 'complete' && c.status !== 'deleted').sort((a, b) => (statusOrder[a.status] ?? 9) - (statusOrder[b.status] ?? 9));
  const [selectedCardIds, setSelectedCardIds] = useState(new Set());

  const toggleCardSelection = (e, cardId) => {
    e.stopPropagation();
    setSelectedCardIds(prev => {
      const next = new Set(prev);
      if (next.has(cardId)) { next.delete(cardId); }
      else if (next.size < 4) { next.add(cardId); }
      else { toast.error('You can run a maximum of 4 cards at once'); }
      return next;
    });
  };
  const completedCards = cards.filter(c => c.status === 'complete');

  const handleDeleteCard = async (e, cardId) => {
    e.stopPropagation();
    if (!await confirm({ title: 'Delete this card and all its viewings?', destructive: true })) return;
    try {
      await api.put(`/sale-cards/${cardId}`, { status: 'deleted' });
      invalidateCards();
      toast.success('Card deleted');
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <SidebarLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/sales?tab=live')}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="font-serif text-2xl font-semibold">Card Management</h1>
              <p className="text-sm text-muted-foreground">{saleNameResolved}</p>
            </div>
          </div>
          {!viewOnly && (
          <div className="flex flex-wrap gap-2 self-end sm:self-auto">
            <Button variant="outline" size="sm" onClick={() => setOcrOpen(true)}>
              <Upload className="w-4 h-4 mr-2" /> Scan Card
            </Button>
            {isDigitalSale && (
              <Button variant="outline" size="sm" onClick={() => setVirtualOpen(true)}
                className="border-primary text-primary hover:bg-primary hover:text-white">
                <Plus className="w-4 h-4 mr-2" /> New Virtual Inquiry
              </Button>
            )}
            <Button size="sm" onClick={() => setCreateOpen(true)} className="bg-primary hover:bg-primary/90 text-white">
              <Plus className="w-4 h-4 mr-2" /> New Card
            </Button>
          </div>
          )}
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-base">Active Cards ({activeCards.length})</CardTitle>
            {selectedCardIds.size >= 2 && (
              <Button
                size="sm"
                className="h-8 text-xs bg-primary hover:bg-primary/90 text-white gap-1.5"
                data-testid="run-multiple-cards-btn"
                onClick={() => navigate('/multi-card-runner', {
                  state: { cardIds: Array.from(selectedCardIds), saleId, saleName: saleNameResolved || '' }
                })}
              >
                <Play className="w-3.5 h-3.5" /> Run {selectedCardIds.size} Cards
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : activeCards.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No active cards. Click &quot;New Card&quot; to create one.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8 pr-0"></TableHead>
                    <TableHead>Viewer</TableHead>
                    <TableHead>Industry Role</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Card Date</TableHead>
                    <TableHead>Hips</TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead></TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activeCards.map(card => {
                    const badge = statusBadge[card.status] || statusBadge.pending;
                    const isVirtual = card.card_type === 'virtual_enquiry';
                    const isSelected = selectedCardIds.has(card.id);
                    return (
                      <TableRow key={card.id} className={`cursor-pointer hover:bg-muted/50 ${isSelected ? 'bg-primary/5' : ''}`} onClick={() => navigate(`/sales/${saleId}/cards/${card.id}`)}>
                        <TableCell className="pr-0 pl-2" onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={e => toggleCardSelection(e, card.id)}
                            disabled={isVirtual || (!isSelected && selectedCardIds.size >= 4)}
                            className="w-4 h-4 accent-primary cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                            data-testid={`select-card-${card.id}`}
                            title={isVirtual ? 'Virtual cards cannot be run' : selectedCardIds.size >= 4 && !isSelected ? 'Maximum 4 cards' : ''}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{card.viewer_name || 'Unknown'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground capitalize">{card.viewer_role?.replace(/_/g, ' ') || (isVirtual ? (card.contact_method || 'virtual').replace(/_/g,' ') : '-')}</TableCell>
                        <TableCell>
                          {isVirtual ? (
                            <Badge className="text-xs bg-green-100 text-green-700 border border-primary/30">Virtual Inquiry</Badge>
                          ) : card.card_type !== 'individual' ? (
                            <Badge variant="outline" className="text-xs capitalize">{card.card_type.replace('_', ' ')}</Badge>
                          ) : 'Individual'}
                        </TableCell>
                        <TableCell><Badge className={badge.className}>{badge.label}</Badge></TableCell>
                        <TableCell className="text-xs text-muted-foreground">{card.date ? new Date(`${card.date}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '-'}</TableCell>
                        <TableCell>{card.viewing_count || 0}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{card.completed_count || 0}/{card.viewing_count || 0}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{card.created_at ? new Date(card.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '-'}</TableCell>
                        <TableCell>
                          {!isVirtual && (
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={async (e) => {
                              e.stopPropagation();
                              navigate(`/sales/${saleId}/cards/${card.id}?run=true`);
                            }}>
                              <Play className="w-3 h-3 mr-1" /> Run Card
                            </Button>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-red-600" onClick={(e) => handleDeleteCard(e, card.id)} title="Delete Card">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Completed Cards */}
        {completedCards.length > 0 && (
          <Card>
            <CardHeader className="cursor-pointer" onClick={() => setCompletedOpen(!completedOpen)}>
              <div className="flex items-center gap-2">
                {completedOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                <CardTitle className="text-base">Completed Cards ({completedCards.length})</CardTitle>
              </div>
            </CardHeader>
            {completedOpen && (
            <CardContent>
              <div className="relative max-w-sm mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Search viewer, role, type..." value={completedSearch} onChange={(e) => setCompletedSearch(e.target.value)} className="pl-9 h-8 text-sm" />
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Viewer</TableHead>
                    <TableHead>Industry Role</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Hips</TableHead>
                    <TableHead>Card Date</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Completed</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {completedCards.filter(card => {
                    if (!completedSearch) return true;
                    const q = completedSearch.toLowerCase();
                    return (card.viewer_name || '').toLowerCase().includes(q) ||
                           (card.viewer_role || '').replace(/_/g, ' ').toLowerCase().includes(q) ||
                           (card.card_type || '').replace(/_/g, ' ').toLowerCase().includes(q);
                  }).map(card => (
                    <TableRow key={card.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/sales/${saleId}/cards/${card.id}`)}>
                      <TableCell className="font-medium">{card.viewer_name}</TableCell>
                      <TableCell className="text-xs text-muted-foreground capitalize">{card.viewer_role?.replace(/_/g, ' ') || '-'}</TableCell>
                      <TableCell>
                        {card.card_type !== 'individual' ? (
                          <Badge variant="outline" className="text-xs capitalize">{card.card_type.replace(/_/g, ' ')}</Badge>
                        ) : 'Individual'}
                      </TableCell>
                      <TableCell>{card.viewing_count}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{card.date ? new Date(`${card.date}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '-'}</TableCell>
                      <TableCell className="text-xs">{card.created_at ? new Date(card.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '-'}</TableCell>
                      <TableCell className="text-xs">{(card.completed_at || card.updated_at) ? new Date(card.completed_at || card.updated_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
            )}
          </Card>
        )}
        {/* AI Scan Learning Corrections */}
        {isSystemOwnerImpersonating && <OcrCorrectionsPanel />}
      </div>

      {/* Create Card Dialog */}
      <CreateCardDialog open={createOpen} onOpenChange={setCreateOpen} saleId={saleId} isDigitalSale={isDigitalSale} onCreated={() => { setCreateOpen(false); invalidateCards(); }} />

      {/* Virtual Inquiry Dialog */}
      {isDigitalSale && (
        <VirtualInquiryDialog open={virtualOpen} onOpenChange={setVirtualOpen} saleId={saleId} onCreated={() => { setVirtualOpen(false); invalidateCards(); }} />
      )}

      {/* OCR Dialog */}
      <OcrUploadDialog open={ocrOpen} onOpenChange={setOcrOpen} saleId={saleId} onCreated={() => { setOcrOpen(false); invalidateCards(); }} />
    </SidebarLayout>
  );
};

/* --- OCR Corrections Panel --- */
const OcrCorrectionsPanel = () => {
  const [corrections, setCorrections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const fetchCorrections = async () => {
    setLoading(true);
    try {
      const res = await api.get('/sale-cards/ocr-corrections');
      setCorrections(res.data || []);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { if (open) fetchCorrections(); }, [open]);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/sale-cards/ocr-corrections/${id}`);
      setCorrections(prev => prev.filter(c => c.id !== id));
      toast.success('Correction removed');
    } catch { toast.error('Failed to delete'); }
  };

  const handleClearAll = async () => {
    if (!await confirm({ title: 'Clear all AI scan corrections? The AI will start fresh without any learned patterns.', destructive: true })) return;
    try {
      await api.delete('/sale-cards/ocr-corrections');
      setCorrections([]);
      toast.success('All corrections cleared');
    } catch { toast.error('Failed to clear'); }
  };

  const nameCorrections = corrections.filter(c => c.field === 'viewer_name');
  const otherCorrections = corrections.filter(c => c.field !== 'viewer_name');

  return (
    <Card className="border-dashed">
      <CardHeader className="pb-2 cursor-pointer" onClick={() => setOpen(o => !o)}>
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            AI Scan Learning Corrections
            {corrections.length > 0 && !loading && open && (
              <Badge variant="secondary" className="text-xs">{corrections.length}</Badge>
            )}
          </CardTitle>
          <span className="text-xs text-muted-foreground">{open ? 'Hide ▲' : 'View ▼'}</span>
        </div>
        <p className="text-xs text-muted-foreground">Corrections saved from previous scans that teach the AI how to read your cards. Remove any incorrect entries here.</p>
      </CardHeader>
      {open && (
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
          ) : corrections.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No corrections saved yet.</p>
          ) : (
            <div className="space-y-3">
              {nameCorrections.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Viewer Name</p>
                  <div className="space-y-1">
                    {nameCorrections.map(c => (
                      <div key={c.id} className="flex items-center justify-between p-2 border rounded text-sm gap-2">
                        <span className="text-muted-foreground truncate">
                          <span className="line-through">{c.original}</span>
                          <span className="mx-2 text-muted-foreground">→</span>
                          <span className="font-medium text-foreground">{c.corrected}</span>
                        </span>
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-destructive shrink-0" onClick={() => handleDelete(c.id)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {otherCorrections.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Other ({otherCorrections.length})</p>
                  <div className="space-y-1">
                    {otherCorrections.map(c => (
                      <div key={c.id} className="flex items-center justify-between p-2 border rounded text-sm gap-2">
                        <span className="text-muted-foreground truncate">[{c.field}] <span className="line-through">{c.original}</span> → <span className="font-medium text-foreground">{c.corrected}</span></span>
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-destructive shrink-0" onClick={() => handleDelete(c.id)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <Button size="sm" variant="outline" className="text-xs text-destructive border-destructive/30" onClick={handleClearAll}>
                Clear All Corrections
              </Button>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

/* --- Create Card Dialog --- */
const CreateCardDialog = ({ open, onOpenChange, saleId, isDigitalSale, onCreated }) => {
  const [viewerName, setViewerName] = useState('');
  const [viewerEntityId, setViewerEntityId] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedSessions, setSelectedSessions] = useState(new Set());
  const [selectedOpps, setSelectedOpps] = useState(new Set());
  const [cardType, setCardType] = useState('individual');
  const [saving, setSaving] = useState(false);
  const [quickCreate, setQuickCreate] = useState(false);
  const [newType, setNewType] = useState('person');
  const [newFirst, setNewFirst] = useState('');
  const [newLast, setNewLast] = useState('');
  const [newOrgName, setNewOrgName] = useState('');
  const [newRole, setNewRole] = useState('');
  const [allContacts, setAllContacts] = useState([]);
  const _searchTimer1 = useRef(null);

  // Opportunities for the "Create Card" dialog — lazy, gated on dialog open
  const { data: opportunityData, isLoading: loadingOpps } = useQuery({
    queryKey: [...QK.saleOpportunities(saleId), 'card-create'],
    queryFn: async () => {
      const [oppsRes, sessionsRes, contactsRes] = await Promise.all([
        api.get(`/sale-opportunities?sale_id=${saleId}`),
        api.get(`/sales/${saleId}/sessions`),
        api.get('/crm-entities'),
      ]);
      const opps = oppsRes.data || [];
      const enriched = await Promise.all(
        opps
          .filter(o => o.entry_status === 'confirmed'
                    && o.result !== 'withdrawn'
                    && o.entry_status !== 'withdrawn'
                    && o.result !== 'out'
                    && o.entry_status !== 'out')
          .sort((a, b) => (parseInt(a.hip_number) || 9999) - (parseInt(b.hip_number) || 9999))
          .map(async (opp) => {
        try {
          const h = await api.get(`/horses/${opp.horse_id}`);
          return { ...opp, horse_gender: h.data?.gender, horse_age: h.data?.age, horse_name: h.data?.system_name || h.data?.name };
        } catch { return { ...opp, horse_gender: '', horse_age: '', horse_name: opp.opportunity_name?.split(' - ')[1] || '' }; }
      }));
      return { opportunities: enriched, sessions: sessionsRes.data || [], contacts: contactsRes.data || [] };
    },
    enabled: open && !!saleId,
  });

  useEffect(() => {
    if (open) {
      // Reset all form fields to blank state every time the dialog opens.
      // Without this, the form retains the previous card's values (bug: viewer, hips, sessions all pre-filled).
      setViewerName('');
      setViewerEntityId(null);
      setSelectedOpps(new Set());
      setSelectedSessions(new Set());
      setCardType('individual');
      setQuickCreate(false);
      setNewType('person');
      setNewFirst('');
      setNewLast('');
      setNewOrgName('');
      setNewRole('');
      setSearchResults([]);
      setAllContacts(opportunityData?.contacts || []);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const opportunities = opportunityData?.opportunities || [];
  const sessions      = opportunityData?.sessions      || [];

  const searchCRM = (query) => {
    if (query.length < 1) { setSearchResults([]); return; }
    clearTimeout(_searchTimer1.current);
    _searchTimer1.current = setTimeout(async () => {
      try {
        const res = await api.get(`/crm-entities?search=${encodeURIComponent(query)}`);
        setSearchResults(res.data.slice(0, 10));
      } catch { setSearchResults([]); }
    }, 200);
  };

  const handleQuickCreate = async () => {
    const isPerson = newType === 'person';
    const displayName = isPerson ? `${newFirst} ${newLast}`.trim() : newOrgName.trim();
    if (isPerson && (!newFirst || !newLast)) { toast.error('First and last name required'); return; }
    if (!isPerson && !newOrgName) { toast.error('Organization name required'); return; }

    // Check for duplicate
    const duplicate = allContacts.find(c => c.system_name?.toLowerCase() === displayName.toLowerCase());
    if (duplicate) {
      toast.error(`"${duplicate.system_name}" already exists. Please select from the search results.`);
      setQuickCreate(false);
      setViewerName(duplicate.system_name);
      setViewerEntityId(duplicate.id);
      return;
    }
    setSaving(true);
    try {
      const payload = isPerson
        ? { crm_type: 'person', crm_class: 'contact', person_first_name: newFirst, person_last_name: newLast, person_industry_role: newRole || null, system_name: displayName }
        : { crm_type: 'organization', crm_class: 'contact', organization_name: newOrgName, organization_industry_role: newRole || null, system_name: displayName };
      const res = await api.post('/crm-entities', payload);
      setViewerEntityId(res.data.id);
      setViewerName(displayName);
      setAllContacts(prev => [...prev, res.data]);
      setQuickCreate(false);
      toast.success(`${isPerson ? 'Contact' : 'Organization'} created`);
    } catch { toast.error('Failed to create'); }
    setSaving(false);
  };

  const selectBulk = (type) => {
    setCardType(type);
    const filtered = visibleOpportunities.filter(opp => {
      if (type === 'all_show') return true;
      if (type === 'all_colts') return opp.horse_gender === 'Colt';
      if (type === 'all_fillies') return opp.horse_gender === 'Filly';
      if (type === 'all_yearlings') return opp.horse_age === 'Yearling' || opp.sold_as === 'yearling';
      if (type === 'all_weanlings') return opp.horse_age === 'Weanling' || opp.sold_as === 'weanling';
      return false;
    });
    setSelectedOpps(new Set(filtered.map(o => o.id)));
  };

  const handleCreate = async () => {
    if (!viewerEntityId) { toast.error('Please select or create a viewer from the CRM'); return; }
    if (selectedOpps.size === 0) { toast.error('Please select at least one hip'); return; }
    setSaving(true);
    try {
      await api.post('/sale-cards', {
        sale_id: saleId,
        viewer_crm_entity_id: viewerEntityId,
        viewer_name: viewerName,
        date,
        session_dates: Array.from(selectedSessions),
        card_type: cardType,
        opportunity_ids: Array.from(selectedOpps),
      });
      toast.success('Card created');
      onCreated();
    } catch { toast.error('Failed to create card'); }
    setSaving(false);
  };

  // Build session ID set from selected session names so we can filter opportunities
  const selectedSessionIds = new Set(
    sessions
      .filter(s => selectedSessions.has(s.name))
      .map(s => s.id)
  );

  // When sessions are selected, only show hips assigned to those sessions
  const visibleOpportunities = selectedSessionIds.size > 0
    ? opportunities.filter(o => {
        const sid = o.sale_session_id || o.session_id;
        return sid && selectedSessionIds.has(sid);
      })
    : opportunities;

  const hasColts = visibleOpportunities.some(o => o.horse_gender === 'Colt');
  const hasFillies = visibleOpportunities.some(o => o.horse_gender === 'Filly');
  const hasYearlings = visibleOpportunities.some(o => o.horse_age === 'Yearling' || o.sold_as === 'yearling');
  const hasWeanlings = visibleOpportunities.some(o => o.horse_age === 'Weanling' || o.sold_as === 'weanling');

  // Re-apply bulk sex/age filter when session selection changes so both orderings work:
  // "session then All Fillies" AND "All Fillies then session" produce the same result.
  useEffect(() => {
    if (!cardType || cardType === 'individual') return;
    const filtered = visibleOpportunities.filter(opp => {
      if (cardType === 'all_show') return true;
      if (cardType === 'all_colts') return opp.horse_gender === 'Colt';
      if (cardType === 'all_fillies') return opp.horse_gender === 'Filly';
      if (cardType === 'all_yearlings') return opp.horse_age === 'Yearling' || opp.sold_as === 'yearling';
      if (cardType === 'all_weanlings') return opp.horse_age === 'Weanling' || opp.sold_as === 'weanling';
      return false;
    });
    setSelectedOpps(new Set(filtered.map(o => o.id)));
  // selectedSessions is always a new Set reference when toggled so React detects changes.
  // visibleOpportunities recalculates before this effect runs — always current.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSessions, cardType]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Create New Card</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          {/* Date & Sessions */}
          <div className={`grid gap-4 ${isDigitalSale ? 'grid-cols-1' : 'grid-cols-2'}`}>
            <div className="space-y-2">
              <Label>Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={date ? 'date-filled' : ''} />
            </div>
            {!isDigitalSale && (
            <div className="space-y-2">
              <Label>Session Date(s)</Label>
              {sessions.length === 0 ? (
                <p className="text-xs text-muted-foreground">No sessions available</p>
              ) : (
                <div className="space-y-1 border rounded p-2 max-h-28 overflow-y-auto">
                  {sessions.sort((a, b) => new Date(a.date) - new Date(b.date)).map(s => (
                    <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/30 p-1 rounded">
                      <Checkbox
                        checked={selectedSessions.has(s.name)}
                        onCheckedChange={() => setSelectedSessions(prev => {
                          const next = new Set(prev);
                          next.has(s.name) ? next.delete(s.name) : next.add(s.name);
                          return next;
                        })}
                      />
                      <span>{s.name}</span>
                      <span className="text-xs text-muted-foreground ml-auto">
                        {new Date(s.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            )}
          </div>

          {/* Viewer */}
          <div className="space-y-2">
            <Label>Viewer *</Label>
            {!quickCreate ? (
              <div className="space-y-2">
                {viewerEntityId ? (
                  /* Linked — show badge + change button */
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-sm">{viewerName}</Badge>
                    <Button variant="ghost" size="sm" className="h-7 text-xs"
                      onClick={async () => { setViewerEntityId(null); setViewerName(''); setSearchResults([]); }}>
                      Change
                    </Button>
                  </div>
                ) : (
                  /* Not linked — search only */
                  <>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        className="pl-9"
                        placeholder="Search by name..."
                        value={viewerName}
                        onChange={async (e) => { setViewerName(e.target.value); setViewerEntityId(null); searchCRM(e.target.value); }}
                        autoFocus
                      />
                    </div>
                    {searchResults.length > 0 && (
                      <div className="border rounded max-h-32 overflow-y-auto">
                        {searchResults.map(e => (
                          <div key={e.id} className="p-2 text-sm hover:bg-muted/50 cursor-pointer flex items-center justify-between gap-2" onClick={async () => {
                            setViewerEntityId(e.id); setViewerName(e.system_name); setSearchResults([]);
                          }}>
                            <span>{e.system_name} <span className="text-xs text-muted-foreground ml-1 capitalize">{(e.person_industry_role || e.organization_industry_role || '').replace(/_/g,' ')}</span></span>
                            <Badge variant="outline" className="text-[10px] shrink-0">{e.crm_class === 'account' ? 'Account' : e.crm_type === 'organization' ? 'Org' : 'Contact'}</Badge>
                          </div>
                        ))}
                      </div>
                    )}
                    {viewerName.length > 0 && searchResults.length === 0 && (
                      <p className="text-xs text-muted-foreground">No matches — use "Create New Contact" to add them.</p>
                    )}
                  </>
                )}
                <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => setQuickCreate(true)}>+ Create New Contact</Button>
              </div>
            ) : (
              <div className="space-y-2 p-3 border rounded">
                {/* Person / Organization toggle */}
                <div className="flex gap-1">
                  <Button size="sm" type="button"
                    variant={newType === 'person' ? 'default' : 'outline'}
                    className="h-7 text-xs flex-1"
                    onClick={() => setNewType('person')}>Person</Button>
                  <Button size="sm" type="button"
                    variant={newType === 'organization' ? 'default' : 'outline'}
                    className="h-7 text-xs flex-1"
                    onClick={() => setNewType('organization')}>Organization</Button>
                </div>
                {newType === 'person' ? (
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="First Name *" value={newFirst} onChange={(e) => setNewFirst(e.target.value)} />
                    <Input placeholder="Last Name *" value={newLast} onChange={(e) => setNewLast(e.target.value)} />
                  </div>
                ) : (
                  <Input placeholder="Organization Name *" value={newOrgName} onChange={(e) => setNewOrgName(e.target.value)} />
                )}
                <Select value={newRole} onValueChange={setNewRole}>
                  <SelectTrigger><SelectValue placeholder="Industry Role (optional)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="owner">Owner</SelectItem>
                    <SelectItem value="trainer">Trainer</SelectItem>
                    <SelectItem value="agent">Agent</SelectItem>
                    <SelectItem value="farm_manager">Farm Manager</SelectItem>
                    <SelectItem value="veterinarian">Veterinarian</SelectItem>
                    <SelectItem value="consignor">Consignor</SelectItem>
                    <SelectItem value="syndicate_member">Syndicate Member</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleQuickCreate} disabled={saving} className="bg-primary hover:bg-primary/90 text-white">Create</Button>
                  <Button size="sm" variant="outline" onClick={() => setQuickCreate(false)}>Cancel</Button>
                </div>
              </div>
            )}
          </div>

          {/* Hip Selection */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Select Hips</Label>
              {selectedSessionIds.size > 0 && (
                <span className="text-xs text-muted-foreground">
                  Showing {visibleOpportunities.length} hip{visibleOpportunities.length !== 1 ? 's' : ''} for selected session{selectedSessionIds.size !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2 mb-2">
              <Button size="sm" variant={cardType === 'all_show' ? 'default' : 'outline'} className="h-7 text-xs" onClick={() => selectBulk('all_show')}>All Show</Button>
              {hasColts && <Button size="sm" variant={cardType === 'all_colts' ? 'default' : 'outline'} className="h-7 text-xs" onClick={() => selectBulk('all_colts')}>All Colts</Button>}
              {hasFillies && <Button size="sm" variant={cardType === 'all_fillies' ? 'default' : 'outline'} className="h-7 text-xs" onClick={() => selectBulk('all_fillies')}>All Fillies</Button>}
              {hasYearlings && <Button size="sm" variant={cardType === 'all_yearlings' ? 'default' : 'outline'} className="h-7 text-xs" onClick={() => selectBulk('all_yearlings')}>All Yearlings</Button>}
              {hasWeanlings && <Button size="sm" variant={cardType === 'all_weanlings' ? 'default' : 'outline'} className="h-7 text-xs" onClick={() => selectBulk('all_weanlings')}>All Weanlings</Button>}
              {cardType !== 'individual' && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={async () => { setCardType('individual'); setSelectedOpps(new Set()); }}>Clear</Button>}
            </div>
            <div className="border rounded max-h-48 overflow-y-auto">
              {visibleOpportunities.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">
                  {selectedSessionIds.size > 0
                    ? 'No hips are assigned to the selected session(s).'
                    : 'No hips available.'}
                </p>
              ) : (
                visibleOpportunities.map(opp => (
                  <div key={opp.id} className="flex items-center gap-3 p-2 hover:bg-muted/30 text-sm">
                    <Checkbox checked={selectedOpps.has(opp.id)} onCheckedChange={() => {
                      setCardType('individual');
                      setSelectedOpps(prev => { const n = new Set(prev); n.has(opp.id) ? n.delete(opp.id) : n.add(opp.id); return n; });
                    }} />
                    <span className="font-medium">#{opp.hip_number || 'TBD'}</span>
                    <span>{opp.horse_name}</span>
                    <span className="text-xs text-muted-foreground ml-auto">{opp.horse_age} {opp.horse_gender}</span>
                  </div>
                ))
              )}
            </div>
            <p className="text-xs text-muted-foreground">{selectedOpps.size} hip(s) selected</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleCreate} disabled={saving} className="bg-primary hover:bg-primary/90 text-white">
            {saving ? 'Creating...' : 'Create Card'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* --- OCR Upload Dialog (with full pre-create editing + AI learning) --- */
const OcrUploadDialog = ({ open, onOpenChange, saleId, onCreated }) => {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);

  // Editable fields (populated from AI result, user can correct before creating)
  const [viewerName, setViewerName] = useState('');
  const [viewerEntityId, setViewerEntityId] = useState(null);
  const [editableDate, setEditableDate] = useState('');
  const [editableHips, setEditableHips] = useState([]); // [{hip_number, status, liked, hip_notes, opportunity_id, included}]

  const [editableTime, setEditableTime] = useState('');
  const [editableNotes, setEditableNotes] = useState('');

  // CRM search
  const [crmQuery, setCrmQuery] = useState('');
  const [allContacts, setAllContacts] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [quickCreate, setQuickCreate] = useState(false);
  const [newType, setNewType] = useState('person');
  const [newFirst, setNewFirst] = useState('');
  const [newLast, setNewLast] = useState('');
  const [newOrgName, setNewOrgName] = useState('');
  const [newRole, setNewRole] = useState('');
  const [savingContact, setSavingContact] = useState(false);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const _searchTimer2 = useRef(null);

  useEffect(() => {
    if (open) {
      api.get('/crm-entities').then(r => setAllContacts(r.data || [])).catch(() => {});
    }
  }, [open]);

  const searchCRM = (query) => {
    setCrmQuery(query);
    setViewerEntityId(null);
    if (query.length < 1) { setSearchResults([]); return; }
    clearTimeout(_searchTimer2.current);
    _searchTimer2.current = setTimeout(async () => {
      try {
        const res = await api.get(`/crm-entities?search=${encodeURIComponent(query)}`);
        setSearchResults(res.data.slice(0, 8));
      } catch { setSearchResults([]); }
    }, 200);
  };

  const selectContact = (entity) => {
    setViewerName(entity.system_name);
    setViewerEntityId(entity.id);
    setCrmQuery('');
    setSearchResults([]);
  };

  const handleQuickCreate = async () => {
    const isPerson = newType === 'person';
    const displayName = isPerson ? `${newFirst} ${newLast}`.trim() : newOrgName.trim();
    if (isPerson && (!newFirst || !newLast)) { toast.error('First and last name required'); return; }
    if (!isPerson && !newOrgName) { toast.error('Organization name required'); return; }
    const dup = allContacts.find(c => c.system_name?.toLowerCase() === displayName.toLowerCase());
    if (dup) { selectContact(dup); setQuickCreate(false); toast.error(`"${dup.system_name}" already exists — selected.`); return; }
    setSavingContact(true);
    try {
      const payload = isPerson
        ? { crm_type: 'person', crm_class: 'contact', person_first_name: newFirst, person_last_name: newLast, person_industry_role: newRole || null, system_name: displayName }
        : { crm_type: 'organization', crm_class: 'contact', organization_name: newOrgName, organization_industry_role: newRole || null, system_name: displayName };
      const res = await api.post('/crm-entities', payload);
      selectContact(res.data);
      setAllContacts(prev => [...prev, res.data]);
      setQuickCreate(false);
      setNewFirst(''); setNewLast(''); setNewOrgName(''); setNewRole('');
      toast.success(`${isPerson ? 'Contact' : 'Organization'} created`);
    } catch { toast.error('Failed to create'); }
    setSavingContact(false);
  };

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    setCameraActive(false);
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      streamRef.current = stream;
      setCameraActive(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {}); // required on iOS to start playback
        }
      }, 100);
    } catch { toast.error('Unable to access camera. Please use Upload Image instead.'); }
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;
    // Guard: video must have valid dimensions — if 0, stream isn't ready yet (common on iOS)
    if (!videoRef.current.videoWidth || !videoRef.current.videoHeight) {
      toast.error('Camera not ready yet — please wait a moment and try again.');
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext('2d').drawImage(videoRef.current, 0, 0);
    canvas.toBlob(async (blob) => {
      stopCamera();
      if (!blob) return;
      setImagePreviewUrl(URL.createObjectURL(blob));
      await processFile(new File([blob], 'capture.jpg', { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.9);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImagePreviewUrl(URL.createObjectURL(file));
    await processFile(file);
  };

  const processFile = async (file) => {
    setUploading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/sale-cards/ocr-upload?sale_id=${saleId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      const crmEntityId = res.data.viewer_crm_entity_id || null;
      setViewerEntityId(crmEntityId);
      setViewerName(res.data.viewer_name || '');

      // If no CRM match, pre-open Create New Contact with parsed name
      if (!crmEntityId && res.data.viewer_name) {
        const parts = res.data.viewer_name.trim().split(/\s+/);
        setNewFirst(parts[0] || '');
        setNewLast(parts.slice(1).join(' ') || '');
        setNewOrgName(res.data.viewer_name.trim()); // pre-fill org name too so toggle works
        setNewRole('');
        setQuickCreate(true);
      }
      setEditableDate(res.data.date || new Date().toISOString().split('T')[0]);
      setEditableTime(res.data.time || new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }));
      setEditableNotes(res.data.general_notes || '');
      const defaultDate = res.data.date || new Date().toISOString().split('T')[0];
      const defaultTime = res.data.time || new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
      setEditableHips((res.data.hip_entries || []).map(h => {
        // OUT = strikethrough/OUT text in notes only — blank line (status:'none', no OUT note) = Listed
        const isOut = !!(h.hip_notes && h.hip_notes.toUpperCase().includes('OUT'));
        // X = Viewed | Slash/Check = Requested | OUT/Strikethrough = not selectable | No mark = Listed
        const mappedStatus = h.status === 'viewed' ? 'viewed' : (h.status === 'requested' ? 'requested' : 'listed');
        return {
          ...h,
          status: mappedStatus,
          is_out: isOut,
          hip_notes: isOut ? (h.hip_notes || '').trim() || 'OUT' : (h.hip_notes || ''),
          included: !!h.opportunity_id && !isOut && (h.status === 'requested' || h.status === 'viewed'),
          view_date: defaultDate,
          view_time: defaultTime,
        };
      }));
      toast.success('Card scanned — please review and correct before creating');
    } catch { toast.error('Card analysis failed. Please try again.'); }
    setUploading(false);
  };

  const updateHip = (idx, field, value) => {
    setEditableHips(prev => prev.map((h, i) => i === idx ? { ...h, [field]: value } : h));
  };

  const handleCreateFromOCR = async () => {
    if (!result) return;
    if (!viewerEntityId) { toast.error('Please select or create a viewer from the CRM'); return; }
    setUploading(true);
    try {
      // Log AI corrections (best-effort, skip if offline)
      if (navigator.onLine) {
        const corrections = [];
        if (viewerName !== result.viewer_name) corrections.push({ field: 'viewer_name', original: result.viewer_name, corrected: viewerName });
        if (editableDate !== result.date) corrections.push({ field: 'date', original: result.date, corrected: editableDate });
        editableHips.forEach((h, i) => {
          const orig = result.hip_entries?.[i];
          if (orig && h.status !== orig.status) corrections.push({ field: 'hip_status', original: orig.status, corrected: h.status, hip: h.hip_number });
          if (orig && h.liked !== orig.liked) corrections.push({ field: 'hip_liked', original: orig.liked, corrected: h.liked, hip: h.hip_number });
        });
        for (const c of corrections) { api.post('/sale-cards/ocr-correction', c).catch(() => {}); }
      }

      const selectedHips = editableHips.filter(h => h.included);
      const oppIds = selectedHips.filter(h => h.opportunity_id).map(h => h.opportunity_id);
      // Build per-hip view_timestamp for completed viewings
      const hipsWithTimestamp = selectedHips.map(h => {
        const ts = h.view_date
          ? new Date(`${h.view_date}T${h.view_time || '00:00'}:00`).toISOString()
          : null;
        return { ...h, view_timestamp: ts };
      });
      const cardPayload = {
        sale_id: saleId,
        viewer_crm_entity_id: viewerEntityId || result.viewer_crm_entity_id,
        viewer_name: viewerName || result.viewer_name,
        date: editableDate,
        time: editableTime,
        session_dates: [],
        card_type: 'individual',
        opportunity_ids: oppIds,
        card_image_file_id: result.card_image_file_id,
        general_notes: editableNotes,
        hip_entries: hipsWithTimestamp,
        auto_create_contact: !viewerEntityId && !result.viewer_crm_entity_id,
      };

      if (!navigator.onLine) {
        // Queue for later sync
        const apiBase = window.location.origin || '';
        await queueMutation({
          method: 'POST',
          url: `${apiBase}/api/sale-cards`,
          body: cardPayload,
          description: `Scan card: ${viewerName || 'Unknown viewer'} — ${editableDate}`,
        });
        toast.success('You\'re offline — card queued and will be created when reconnected');
        handleClose();
        return;
      }

      await api.post('/sale-cards', cardPayload);
      toast.success('Card created from scan');
      handleClose();
      onCreated();
    } catch { toast.error('Failed to create card'); }
    setUploading(false);
  };

  const STATUS_OPTIONS = [
    { value: 'viewed', label: 'Viewed', cls: 'bg-green-100 text-green-700' },
    { value: 'requested', label: 'Requested', cls: 'bg-blue-100 text-blue-700' },
    { value: 'listed', label: 'Listed', cls: 'bg-gray-100 text-gray-600' },
  ];

  const handleClose = () => {
    setResult(null); setImagePreviewUrl(null); stopCamera();
    setCrmQuery(''); setSearchResults([]);
    setViewerName(''); setViewerEntityId(null);
    setEditableDate(''); setEditableTime('');
    setEditableHips([]); setEditableNotes('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col gap-0 p-0">
        <DialogHeader className="px-6 pt-6 pb-3 border-b shrink-0">
          <DialogTitle>Scan Physical Card</DialogTitle>
          <p className="text-xs text-muted-foreground">AI extracts the details — review and correct before saving. All corrections help improve future scans.</p>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">

          {cameraActive ? (
            <div className="space-y-2">
              <video ref={videoRef} autoPlay playsInline muted className="w-full rounded border" />
              <div className="flex gap-2">
                <Button className="flex-1 bg-primary hover:bg-primary/90 text-white" onClick={capturePhoto}>Capture</Button>
                <Button variant="outline" className="flex-1" onClick={stopCamera}>Cancel</Button>
              </div>
            </div>
          ) : !result ? (
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={startCamera} disabled={uploading}>
                <Eye className="w-4 h-4 mr-2" /> Take Photo
              </Button>
              <Button variant="outline" className="flex-1" onClick={() => document.getElementById('ocr-file').click()} disabled={uploading}>
                <Upload className="w-4 h-4 mr-2" /> Upload Image
              </Button>
            </div>
          ) : null}
          <input id="ocr-file" type="file" accept="image/*" onChange={handleUpload} className="hidden" />

          {uploading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-4 justify-center">
              <Loader2 className="w-5 h-5 animate-spin" /> Analyzing card with AI...
            </div>
          )}

          {result && (
            <div className="space-y-4">
              {/* Card image */}
              {(imagePreviewUrl || result.card_image_file_id) && (
                <div className="border rounded overflow-hidden bg-muted/20">
                  <img src={imagePreviewUrl || `${window.location.origin}/api/files/public/${result.card_image_file_id}`}
                    alt="Scanned card" className="w-full max-h-[160px] object-contain" />
                </div>
              )}

              {/* ── Viewer ── */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Viewer Name *</label>
                {!quickCreate ? (
                  <>
                    {viewerEntityId ? (
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-sm">{viewerName}</Badge>
                        <button onClick={async () => { setViewerEntityId(null); setViewerName(''); setCrmQuery(''); }}
                          className="text-xs text-muted-foreground hover:text-foreground underline">Change</button>
                      </div>
                    ) : (
                      <>
                        <div className="relative">
                          <Input
                            value={crmQuery}
                            onChange={e => { setCrmQuery(e.target.value); searchCRM(e.target.value); }}
                            placeholder="Search by name..."
                            className="pr-8"
                          />
                        </div>
                        {crmQuery.length > 0 && searchResults.length === 0 && (
                          <p className="text-xs text-muted-foreground">No matches — use "Create New Contact".</p>
                        )}
                        {searchResults.length > 0 && (
                          <div className="border rounded shadow-md bg-background z-10 max-h-40 overflow-y-auto">
                            {searchResults.map(e => (
                              <button key={e.id} onClick={() => selectContact(e)}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50 border-b last:border-0 flex items-center justify-between gap-2">
                                <span>
                                  <span className="font-medium">{e.system_name}</span>
                                  <span className="text-xs text-muted-foreground capitalize ml-1">{(e.person_industry_role || e.organization_industry_role || '').replace(/_/g,' ')}</span>
                                </span>
                                <Badge variant="outline" className="text-[10px] shrink-0">{e.crm_class === 'account' ? 'Account' : e.crm_type === 'organization' ? 'Org' : 'Contact'}</Badge>
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                    <Button variant="link" size="sm" className="h-auto p-0 text-xs"
                      onClick={async () => { setQuickCreate(true); setNewType('person'); setNewFirst(''); setNewLast(''); setNewOrgName(''); setNewRole(''); }}>
                      + Create New Contact
                    </Button>
                  </>
                ) : (
                  <div className="space-y-2 p-3 border rounded">
                    <div className="flex gap-1">
                      <Button size="sm" type="button" variant={newType === 'person' ? 'default' : 'outline'} className="h-7 text-xs flex-1" onClick={() => setNewType('person')}>Person</Button>
                      <Button size="sm" type="button" variant={newType === 'organization' ? 'default' : 'outline'} className="h-7 text-xs flex-1" onClick={() => setNewType('organization')}>Organization</Button>
                    </div>
                    {newType === 'person' ? (
                      <div className="grid grid-cols-2 gap-2">
                        <Input placeholder="First Name *" value={newFirst} onChange={e => setNewFirst(e.target.value)} />
                        <Input placeholder="Last Name *" value={newLast} onChange={e => setNewLast(e.target.value)} />
                      </div>
                    ) : (
                      <Input placeholder="Organization Name *" value={newOrgName} onChange={e => setNewOrgName(e.target.value)} />
                    )}
                    <Select value={newRole} onValueChange={setNewRole}>
                      <SelectTrigger><SelectValue placeholder="Industry Role (optional)" /></SelectTrigger>
                      <SelectContent>
                        {['owner','trainer','agent','farm_manager','veterinarian','consignor','syndicate_member','bloodstock_agent','jockey','other'].map(r => (
                          <SelectItem key={r} value={r}>{r.replace(/_/g,' ').replace(/\b\w/g, c => c.toUpperCase())}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={handleQuickCreate} disabled={savingContact} className="bg-primary hover:bg-primary/90 text-white">
                        {savingContact ? 'Creating...' : 'Create'}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setQuickCreate(false)}>Cancel</Button>
                    </div>
                  </div>
                )}
              </div>

              {/* ── Date + Time ── */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Date</label>
                  <Input type="date" value={editableDate} onChange={e => {
                    const d = e.target.value;
                    setEditableDate(d);
                    setEditableHips(prev => prev.map(h => ({ ...h, view_date: d })));
                  }} />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Time</label>
                  <Input type="time" value={editableTime} onChange={e => {
                    const t = e.target.value;
                    setEditableTime(t);
                    setEditableHips(prev => prev.map(h => ({ ...h, view_time: t })));
                  }} className="text-foreground" style={{ color: '#111827', WebkitTextFillColor: '#111827' }} />
                </div>
              </div>

              {/* ── Hip Entries ── */}
              {editableHips.length > 0 && (
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Viewings ({editableHips.filter(h => h.included).length} of {editableHips.length} selected)
                  </label>
                  <div className="border rounded overflow-hidden divide-y">
                    {editableHips.map((h, i) => (
                      <div key={i} className={`p-2 space-y-2 ${!h.opportunity_id ? 'opacity-40 bg-muted/10 pointer-events-none' : (h.is_out ? 'opacity-60 bg-muted/20' : (!h.included ? 'opacity-50 bg-muted/20' : ''))}`}>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={h.included}
                            onCheckedChange={v => updateHip(i, 'included', !!v)}
                            disabled={!h.opportunity_id || h.is_out}
                            title={!h.opportunity_id ? 'Hip not entered as a sale opportunity in the system' : h.is_out ? 'This hip is marked OUT' : ''}
                          />
                          <span className="font-medium text-sm w-12">#{h.hip_number}</span>
                          {!h.opportunity_id && (
                            <span className="text-[10px] text-muted-foreground italic">not in system</span>
                          )}
                          {h.is_out ? (
                            <span className="flex-1 h-7 flex items-center px-2 text-xs text-muted-foreground bg-muted/40 rounded border select-none">OUT</span>
                          ) : (
                            <Select value={h.status} onValueChange={v => updateHip(i, 'status', v)}>
                              <SelectTrigger className="flex-1 h-7 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {STATUS_OPTIONS.map(o => (
                                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          {/* Liked — clickable heart, no checkbox */}
                          <button
                            type="button"
                            onClick={() => updateHip(i, 'liked', !h.liked)}
                            className={`p-1 rounded transition-colors ${h.liked ? 'text-red-500' : 'text-muted-foreground hover:text-red-400'}`}
                            title="Liked"
                          >
                            <Heart className={`w-4 h-4 ${h.liked ? 'fill-current' : ''}`} />
                          </button>
                          {/* Viewed vet report — clickable eye, no checkbox */}
                          <button
                            type="button"
                            onClick={() => updateHip(i, 'viewed_vet_report', !h.viewed_vet_report)}
                            className={`p-1 rounded transition-colors ${h.viewed_vet_report ? 'text-blue-500' : 'text-muted-foreground hover:text-blue-400'}`}
                            title="Viewed vet report"
                          >
                            <Eye className={`w-4 h-4 ${h.viewed_vet_report ? 'fill-current opacity-80' : ''}`} />
                          </button>
                        </div>
                        <Input
                          value={h.hip_notes || ''}
                          onChange={e => updateHip(i, 'hip_notes', e.target.value)}
                          placeholder="Notes for this viewing…"
                          className="h-7 text-xs"
                        />
                        {/* Per-hip viewed date/time — only shown for included hips */}
                        {h.included && (
                          <div className="flex items-center gap-2">
                            <Clock className="w-3 h-3 text-foreground shrink-0" />
                            <input
                              type="date"
                              value={h.view_date || ''}
                              onChange={e => updateHip(i, 'view_date', e.target.value)}
                              className="h-6 text-xs border rounded px-1 bg-background w-28"
                              style={{ color: '#111827', WebkitTextFillColor: '#111827' }}
                              title="Viewed date"
                            />
                            <input
                              type="time"
                              value={h.view_time || ''}
                              onChange={e => updateHip(i, 'view_time', e.target.value)}
                              className="h-6 text-xs border rounded px-1 bg-background w-20"
                              style={{ color: '#111827', WebkitTextFillColor: '#111827' }}
                              title="Viewed time"
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── General Notes — always shown, editable ── */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">General Notes</label>
                <textarea
                  value={editableNotes}
                  onChange={e => setEditableNotes(e.target.value)}
                  placeholder="General notes from the card scan…"
                  rows={2}
                  className="w-full border rounded px-3 py-2 text-sm bg-background resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t shrink-0 flex justify-end gap-2">
          <Button variant="outline" onClick={handleClose}>Cancel</Button>
          {result && (
            <Button onClick={handleCreateFromOCR} disabled={uploading} className="bg-primary hover:bg-primary/90 text-white">
              {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating…</> : 'Create Card from Scan'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* --- Virtual Inquiry Dialog --- */
const CONTACT_METHODS = [
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone Call' },
  { value: 'text', label: 'Text / SMS' },
  { value: 'online_chat', label: 'Online Chat' },
  { value: 'other', label: 'Other' },
];

const VirtualInquiryDialog = ({ open, onOpenChange, saleId, onCreated }) => {
  const [inquirerName, setInquirerName] = useState('');
  const [inquirerEntityId, setInquirerEntityId] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [allContacts, setAllContacts] = useState([]);
  const [quickCreate, setQuickCreate] = useState(false);
  const [newFirst, setNewFirst] = useState('');
  const [newLast, setNewLast] = useState('');
  const [newRole, setNewRole] = useState('');
  const [contactMethod, setContactMethod] = useState('email');
  const [generalNotes, setGeneralNotes] = useState('');
  const [opportunities, setOpportunities] = useState([]);
  const [hipData, setHipData] = useState({});
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const _searchTimer3 = useRef(null);

  useEffect(() => {
    if (!open) return;
    Promise.all([
      api.get(`/sale-opportunities?sale_id=${saleId}`),
      api.get('/crm-entities'),
    ]).then(([oppsRes, contactsRes]) => {
      const opps = (oppsRes.data?.items || oppsRes.data || [])
        .filter(o => o.entry_status === 'confirmed'
                  && o.result !== 'withdrawn'
                  && o.entry_status !== 'withdrawn'
                  && o.result !== 'out'
                  && o.entry_status !== 'out')
        .sort((a, b) =>
        (parseInt(a.hip_number) || 9999) - (parseInt(b.hip_number) || 9999)
      );
      setOpportunities(opps);
      setAllContacts(contactsRes.data || []);
    }).catch(() => {});
    // Reset form
    setInquirerName(''); setInquirerEntityId(null); setSearchResults([]);
    setContactMethod('email'); setGeneralNotes('');
    setHipData({}); setSearch(''); setQuickCreate(false);
    setNewFirst(''); setNewLast(''); setNewRole('');
  }, [open, saleId]);

  const searchCRM = (query) => {
    if (query.length < 1) { setSearchResults([]); return; }
    clearTimeout(_searchTimer3.current);
    _searchTimer3.current = setTimeout(async () => {
      try {
        const res = await api.get(`/crm-entities?search=${encodeURIComponent(query)}`);
        setSearchResults(res.data.slice(0, 10));
      } catch { setSearchResults([]); }
    }, 200);
  };

  const handleQuickCreate = async () => {
    if (!newFirst || !newLast) { toast.error('First and last name required'); return; }
    const fullName = `${newFirst} ${newLast}`.toLowerCase();
    const duplicate = allContacts.find(c => c.system_name?.toLowerCase() === fullName);
    if (duplicate) {
      setQuickCreate(false);
      setInquirerName(duplicate.system_name);
      setInquirerEntityId(duplicate.id);
      toast.error(`"${duplicate.system_name}" already exists — selected.`);
      return;
    }
    setSaving(true);
    try {
      const res = await api.post('/crm-entities', {
        crm_type: 'person', crm_class: 'contact',
        person_first_name: newFirst, person_last_name: newLast,
        person_industry_role: newRole || null,
        system_name: `${newFirst} ${newLast}`,
      });
      setInquirerEntityId(res.data.id);
      setInquirerName(`${newFirst} ${newLast}`);
      setAllContacts(prev => [...prev, res.data]);
      setQuickCreate(false);
      toast.success('Contact created');
    } catch { toast.error('Failed to create contact'); }
    setSaving(false);
  };

  const toggleHip = (oppId) => {
    setHipData(prev => ({
      ...prev,
      [oppId]: { ...prev[oppId], selected: !prev[oppId]?.selected }
    }));
  };

  const updateHip = (oppId, field, value) => {
    setHipData(prev => ({ ...prev, [oppId]: { ...prev[oppId], [field]: value } }));
  };

  const selectedOpps = opportunities.filter(o => hipData[o.id]?.selected);

  const handleCreate = async () => {
    if (!inquirerName.trim()) { toast.error('Inquirer name is required'); return; }
    if (selectedOpps.length === 0) { toast.error('Please select at least one hip'); return; }
    setSaving(true);
    try {
      const virtualHipDataPayload = {};
      selectedOpps.forEach(o => {
        virtualHipDataPayload[o.id] = { liked: !!hipData[o.id]?.liked, notes: hipData[o.id]?.notes || '' };
      });
      await api.post('/sale-cards', {
        sale_id: saleId,
        viewer_name: inquirerName.trim(),
        viewer_crm_entity_id: inquirerEntityId || null,
        card_type: 'virtual_enquiry',
        contact_method: contactMethod,
        general_notes: generalNotes,
        date: new Date().toISOString().split('T')[0],
        opportunity_ids: selectedOpps.map(o => o.id),
        virtual_hip_data: virtualHipDataPayload,
      });
      toast.success('Virtual inquiry recorded');
      onCreated();
    } catch { toast.error('Failed to save inquiry'); }
    setSaving(false);
  };

  const filteredOpps = opportunities.filter(o => {
    if (!search) return true;
    const name = (o.opportunity_name || '').toLowerCase();
    const hip = String(o.hip_number || '');
    return name.includes(search.toLowerCase()) || hip.includes(search);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>New Virtual Inquiry</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">

          {/* Inquirer Name — search + create new */}
          <div className="space-y-2">
            <Label>Inquirer Name *</Label>
            {!quickCreate ? (
              <div className="space-y-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input className="pl-9" placeholder="Search by name..."
                    value={inquirerName}
                    onChange={e => { setInquirerName(e.target.value); setInquirerEntityId(null); searchCRM(e.target.value); }} />
                </div>
                {searchResults.length > 0 && !inquirerEntityId && (
                  <div className="border rounded max-h-32 overflow-y-auto">
                    {searchResults.map(e => (
                      <div key={e.id} className="p-2 text-sm hover:bg-muted/50 cursor-pointer"
                        onClick={async () => { setInquirerEntityId(e.id); setInquirerName(e.system_name); setSearchResults([]); }}>
                        {e.system_name}
                        <span className="text-xs text-muted-foreground ml-1">{e.person_industry_role || e.organization_industry_role || ''}</span>
                      </div>
                    ))}
                  </div>
                )}
                {inquirerEntityId && <Badge variant="outline" className="text-xs">Linked: {inquirerName}</Badge>}
                <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => setQuickCreate(true)}>+ Create New Contact</Button>
              </div>
            ) : (
              <div className="space-y-2 p-3 border rounded">
                <div className="grid grid-cols-3 gap-2">
                  <Input placeholder="First Name *" value={newFirst} onChange={e => setNewFirst(e.target.value)} />
                  <Input placeholder="Last Name *" value={newLast} onChange={e => setNewLast(e.target.value)} />
                  <Select value={newRole} onValueChange={setNewRole}>
                    <SelectTrigger><SelectValue placeholder="Industry Role" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="owner">Owner</SelectItem>
                      <SelectItem value="trainer">Trainer</SelectItem>
                      <SelectItem value="agent">Agent</SelectItem>
                      <SelectItem value="farm_manager">Farm Manager</SelectItem>
                      <SelectItem value="veterinarian">Veterinarian</SelectItem>
                      <SelectItem value="consignor">Consignor</SelectItem>
                      <SelectItem value="syndicate_member">Syndicate Member</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleQuickCreate} disabled={saving} className="bg-primary hover:bg-primary/90 text-white">Create</Button>
                  <Button size="sm" variant="outline" onClick={() => setQuickCreate(false)}>Cancel</Button>
                </div>
              </div>
            )}
          </div>

          {/* Contact Method */}
          <div className="space-y-2">
            <Label>Contact Method *</Label>
            <Select value={contactMethod} onValueChange={setContactMethod}>
              <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CONTACT_METHODS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* General Notes */}
          <div className="space-y-2">
            <Label>General Notes</Label>
            <Input value={generalNotes} onChange={e => setGeneralNotes(e.target.value)}
              placeholder="Overall inquiry notes (not specific to a hip)" />
          </div>

          {/* Hip Selection */}
          <div className="space-y-2">
            <Label>Hips Inquired About</Label>
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search hips..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-8" />
            </div>
            <div className="border rounded max-h-72 overflow-y-auto">
              {filteredOpps.map(opp => {
                const data = hipData[opp.id] || {};
                const horseName = opp.opportunity_name?.split(' - ').pop() || '-';
                return (
                  <div key={opp.id} className={`p-3 border-b last:border-b-0 ${data.selected ? 'bg-green-50' : 'hover:bg-muted/30'}`}>
                    <div className="flex items-center gap-3">
                      {/* Checkbox — stopPropagation to avoid double-toggle */}
                      <Checkbox
                        checked={!!data.selected}
                        onCheckedChange={() => toggleHip(opp.id)}
                        onClick={e => e.stopPropagation()}
                      />
                      {/* Clicking name/hip also toggles */}
                      <span className="text-sm font-medium cursor-pointer" onClick={() => toggleHip(opp.id)}>#{opp.hip_number || 'TBD'}</span>
                      <span className="text-sm flex-1 cursor-pointer" onClick={() => toggleHip(opp.id)}>{horseName}</span>
                      {data.selected && (
                        <button type="button"
                          className={`p-1 rounded ${data.liked ? 'text-red-500' : 'text-muted-foreground hover:text-red-400'}`}
                          onClick={e => { e.stopPropagation(); updateHip(opp.id, 'liked', !data.liked); }}
                          title={data.liked ? 'Remove like' : 'Mark as interested'}>
                          <Heart className={`w-4 h-4 ${data.liked ? 'fill-current' : ''}`} />
                        </button>
                      )}
                    </div>
                    {data.selected && (
                      <div className="mt-2 pl-7">
                        <Input value={data.notes || ''} onChange={e => updateHip(opp.id, 'notes', e.target.value)}
                          placeholder="Notes for this hip..." className="h-7 text-xs" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {selectedOpps.length > 0 && (
              <p className="text-xs text-muted-foreground">{selectedOpps.length} hip(s) selected</p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleCreate} disabled={saving} className="bg-primary hover:bg-primary/90 text-white">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : 'Record Inquiry'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CardManagementPage;

